import { Component, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { NgFor } from '@angular/common';

type Post = {
  title: string;
  desc: string;
  tag: string;
  tone: 'analytics' | 'bi' | 'engineering' | 'science' | 'ai' | 'governance';
};

@Component({
  selector: 'app-blog-page',
  standalone: true,
  imports: [NgFor],
  template: `
    <main class="page">
      <div class="container">
        <header class="head">
          <h1 class="h1">Insights Data & IA</h1>
          <p class="lead">
            Guides, bonnes pratiques et retours d’expérience pour construire une data stack performante.
          </p>
        </header>

        <!-- MOBILE FILTERS ONLY -->
        <div class="cats">
          <button class="chip"
                  *ngFor="let c of cats"
                  type="button"
                  (click)="scrollTo(c)">
            {{c}}
          </button>
        </div>

        <div class="railWrapper">

          <!-- LEFT ARROW -->
          <button class="arrow left" type="button" aria-label="Article précédent" (click)="scroll(-1)">
            <svg class="arrow__icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M15 6l-6 6 6 6" />
            </svg>
          </button>

          <div class="rail" #rail role="list">
            <article class="card post"
                     role="listitem"
                     *ngFor="let p of posts"
                     [attr.data-tag]="p.tag"
                     [style.background-image]="bgFor(p.tone)">
              <div class="overlay"></div>

              <div class="content">
                <h2 class="h2">{{p.title}}</h2>
                <p class="muted">{{p.desc}}</p>
                <div class="meta">{{p.tag}}</div>
              </div>
            </article>
          </div>

          <!-- RIGHT ARROW -->
          <button class="arrow right" type="button" aria-label="Article suivant" (click)="scroll(1)">
            <svg class="arrow__icon" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>

        </div>
      </div>
    </main>  `,
  styles: [`
    .page{
      padding: 44px 0 70px;
    }

    .head{
      margin-bottom: 10px;
    }

    .lead{
      color: var(--muted);
      font-weight: 750;
      max-width: 900px;
    }

    /* =========================
       CATEGORY BUTTONS (MOBILE ONLY)
       ========================= */

    .cats{
      display: none;
      margin-top: 16px;
      gap: 10px;
      flex-wrap: wrap;
    }

    .chip{
      padding: 8px 14px;
      border-radius: 999px;
      border: 1px solid rgba(255,255,255,.12);
      background: rgba(255,255,255,.05);
      color: var(--text);
      font-weight: 800;
      font-size: 13px;
      cursor: pointer;
      transition: all .15s ease;
    }

    .chip:hover{
      border-color: rgba(255,255,255,.25);
    }

    /* Show only on small screens */
    @media (max-width: 768px){
      .cats{
        display: flex;
      }
    }

    /* =========================
       RAIL WRAPPER
       ========================= */

    .railWrapper{
      position: relative;
      margin-top: 24px;
    }

    /* =========================
       HORIZONTAL RAIL
       ========================= */

    .rail{
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: minmax(320px, 1fr);
      gap: 18px;

      overflow-x: auto;
      overflow-y: hidden;

      scroll-snap-type: x mandatory;
      scroll-behavior: smooth;

      padding-bottom: 14px;
    }

    .rail::-webkit-scrollbar{
      height: 10px;
    }

    .rail::-webkit-scrollbar-thumb{
      background: rgba(255,255,255,.10);
      border-radius: 999px;
    }

    .rail::-webkit-scrollbar-track{
      background: transparent;
    }

    /* =========================
       POST CARD
       ========================= */

    .post{
      scroll-snap-align: start;

      position: relative;
      min-height: 260px;

      border-radius: 26px;
      border: 1px solid rgba(255,255,255,.10);

      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;

      display: flex;
      align-items: flex-end;

      overflow: hidden;

      box-shadow: 0 30px 120px rgba(0,0,0,.45);
      transition: transform .2s ease, box-shadow .2s ease;
    }

    .post:hover{
      transform: translateY(-4px);
      box-shadow: 0 40px 140px rgba(0,0,0,.55);
    }

    /* =========================
       DARK OVERLAY
       ========================= */

    .overlay{
      position: absolute;
      inset: 0;
      background: linear-gradient(
          to top,
          rgba(7,11,23,.96) 15%,
          rgba(7,11,23,.75) 45%,
          rgba(7,11,23,.30) 75%,
          transparent 100%
      );
    }

    /* =========================
       TEXT CONTENT
       ========================= */

    .content{
      position: relative;
      padding: 22px;
      z-index: 2;
    }

    .h2{
      margin: 0;
      color: #ffffff;
    }

    .muted{
      margin-top: 8px;
      color: rgba(235,240,255,.85);
      font-weight: 700;
    }

    .meta{
      margin-top: 14px;
      font-size: 12px;
      font-weight: 900;
      letter-spacing: .4px;
      color: rgba(0,229,168,.95);
    }

    /* =========================
       ARROWS (DESKTOP ONLY)
       ========================= */

    .arrow{
      position: absolute;
      top: 50%;
      transform: translateY(-50%);

      width: 48px;
      height: 48px;

      display: inline-flex;
      align-items: center;
      justify-content: center;

      border-radius: 50%;
      border: 1px solid rgba(255,255,255,.15);

      background: rgba(7,11,23,.88);
      backdrop-filter: blur(8px);

      color: #ffffff;
      cursor: pointer;
      z-index: 6;

      box-shadow: 0 12px 30px rgba(0,0,0,.22);
      transition: all .15s ease;
    }

    .arrow__icon{
      width: 20px;
      height: 20px;
      display: block;
    }

    .arrow__icon path{
      fill: none;
      stroke: currentColor;
      stroke-width: 2.4;
      stroke-linecap: round;
      stroke-linejoin: round;
    }

    .arrow:hover{
      background: rgba(79,125,255,.95);
      border-color: rgba(79,125,255,.95);
      color: #ffffff;
    }

    .arrow.left{
      left: 12px;
    }

    .arrow.right{
      right: 12px;
    }


    :host-context(body[data-theme="light"]) .arrow{
      background: rgba(255,255,255,.96);
      border-color: rgba(15,23,42,.12);
      color: #0f172a;
      box-shadow: 0 10px 24px rgba(15,23,42,.12);
    }

    :host-context(body[data-theme="light"]) .arrow:hover{
      background: #ffffff;
      border-color: rgba(59,130,246,.28);
      color: #0f172a;
    }

    /* Hide arrows on mobile */
    @media (max-width: 768px){
      .arrow{
        display: none;
      }
    }

    /* =========================
       RESPONSIVE TWEAKS
       ========================= */

    @media (max-width: 560px){

      .page{
        padding: 34px 0 50px;
      }

      .rail{
        grid-auto-columns: minmax(240px, 85vw);
        gap: 14px;
      }

      .post{
        min-height: 220px;
      }

      .content{
        padding: 18px;
      }

    }
  `]
})
export class BlogPageComponent implements AfterViewInit {
  @ViewChild('rail') railRef!: ElementRef<HTMLDivElement>;

  active = 'Tous';

  cats = [
    'Data Analytics',
    'Business Intelligence',
    'Data Engineering',
    'Data Science',
    'IA & Automatisation',
    'Gouvernance Data'
  ];

  posts: Post[] = [
    {
      title: '5 KPI que votre direction devrait suivre chaque semaine',
      desc: 'Un set KPI actionnable pour piloter la performance.',
      tag: 'Data Analytics',
      tone: 'analytics'
    },
    {
      title: "Comment éviter les dashboards 'vanity metrics'",
      desc: 'Mettre le focus sur les métriques qui changent vraiment la donne.',
      tag: 'Business Intelligence',
      tone: 'bi'
    },
    {
      title: 'Forecasting sans boîte noire',
      desc: 'Des prévisions explicables et robustes, orientées décisions.',
      tag: 'IA & Automatisation',
      tone: 'ai'
    },
    {
      title: '5 tests simples pour améliorer la qualité des données',
      desc: 'Qualité, détection d’anomalies et alerting dès le départ.',
      tag: 'Data Engineering',
      tone: 'engineering'
    },
    {
      title: 'Passer du reporting à la prédiction',
      desc: 'Roadmap pragmatique : de la BI à l’IA utile.',
      tag: 'Data Science',
      tone: 'science'
    },
    {
      title: 'Gouvernance data : démarrer simple, livrer vite',
      desc: 'Qualité, ownership et règles pragmatiques sans ralentir les équipes.',
      tag: 'Gouvernance Data',
      tone: 'governance'
    }
  ];

  ngAfterViewInit(): void {
    // ensures ViewChild exists
  }

  iconFor(cat: string): string {
    const map: Record<string, string> = {
      'Data Analytics': 'assets/blog-icons/dbs-icon-analytics.png',
      'Business Intelligence': 'assets/blog-icons/dbs-icon-bi.png',
      'Data Engineering': 'assets/blog-icons/dbs-icon-engineering.png',
      'Data Science': 'assets/blog-icons/dbs-icon-science.png',
      'IA & Automatisation': 'assets/blog-icons/dbs-icon-ai.png',
      'Gouvernance Data': 'assets/blog-icons/dbs-icon-governance.png',
    };
    return map[cat] ?? 'assets/blog-icons/dbs-icon-all.png';
  }

  scroll(direction: number) {
    const rail = this.railRef?.nativeElement;
    if (!rail) return;

    const amount = 380; // scroll amount per click
    rail.scrollBy({
      left: direction * amount,
      behavior: 'smooth'
    });
  }

  scrollTo(cat: string) {
    const rail = this.railRef?.nativeElement;
    if (!rail) return;

    const items = Array.from(rail.querySelectorAll<HTMLElement>('.post'));
    const target = items.find(el => el.getAttribute('data-tag') === cat);

    if (!target) return;

    rail.scrollTo({
      left: target.offsetLeft - 10,
      behavior: 'smooth'
    });
  }

  bgFor(tone: Post['tone']): string {
    const map: Record<Post['tone'], string> = {
      analytics: '/assets/blog-icons/dbs-icon-analytics.png',
      bi: '/assets/blog-icons/dbs-icon-bi.png',
      engineering: '/assets/blog-icons/dbs-icon-engineering.png',
      science: '/assets/blog-icons/dbs-icon-science.png',
      ai: '/assets/blog-icons/dbs-icon-ai.png',
      governance: '/assets/blog-icons/dbs-icon-governance.png',
    };

    return `
    radial-gradient(circle at 30% 25%, rgba(79,125,255,.45), transparent 55%),
    radial-gradient(circle at 75% 70%, rgba(0,229,168,.25), transparent 60%),
    url('${map[tone]}')
  `;
  }
}
